// ===========================================
// Parent Permission Slip Detail
// ===========================================
// The authenticated signing page. Same form as the magic-link page; the
// difference is that a logged-in signature also records WHO signed
// (`signedByUserId`), which is stronger evidence than the link path can give.

import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Link as MuiLink,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PageHeader from '../../components/common/PageHeader';
import SlipSigningForm from '../../components/permissionSlips/SlipSigningForm';
import {
  useParentPermissionSlip,
  useSignPermissionSlip,
  useDeclinePermissionSlip,
} from '../../hooks/usePermissionSlips';
import { permissionSlipParentApi, SignSlipInput } from '../../api/permissionSlips.api';
import { openSignedSlipPdf } from '../../components/permissionSlips/downloadSlipPdf';
import { useSnackbar } from 'notistack';
import { useTenant } from '../../contexts/TenantContext';

export default function ParentPermissionSlipDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { schoolSlug } = useTenant();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, isError } = useParentPermissionSlip(id);
  const signMutation = useSignPermissionSlip();
  const declineMutation = useDeclinePermissionSlip();

  const backToList = () =>
    navigate(schoolSlug ? `/${schoolSlug}/parent/permission-slips` : '/parent/permission-slips');

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box>
        <PageHeader
          title="Permission Slip"
          breadcrumbs={[
            { label: 'Dashboard', path: `/${schoolSlug}/parent` },
            { label: 'Permission Slips', path: `/${schoolSlug}/parent/permission-slips` },
            { label: 'Permission Slip' },
          ]}
        />
        <Alert severity="error">
          This permission slip could not be found. It may have been closed by the school.
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={backToList} sx={{ mt: 2 }}>
          Back to Permission Slips
        </Button>
      </Box>
    );
  }

  const { slip, responses } = data;
  const signedResponses = responses.filter((r) => r.status === 'SIGNED' && r.signedPdfUrl);

  const handleSign = async (input: SignSlipInput) => {
    await signMutation.mutateAsync({ slipId: id, data: input });
  };

  const handleDecline = async (studentId: string, parentNotes?: string) => {
    await declineMutation.mutateAsync({ slipId: id, studentId, parentNotes });
  };

  return (
    <Box>
      <PageHeader
        title={slip.title}
        subtitle="Review and give your permission"
        breadcrumbs={[
          { label: 'Dashboard', path: `/${schoolSlug}/parent` },
          { label: 'Permission Slips', path: `/${schoolSlug}/parent/permission-slips` },
          { label: slip.title },
        ]}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <SlipSigningForm
            slip={slip}
            responses={responses}
            onSign={handleSign}
            onDecline={handleDecline}
            isSubmitting={signMutation.isPending || declineMutation.isPending}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          {signedResponses.length > 0 && (
            <Stack spacing={1}>
              {signedResponses.map((r) => (
                <Alert key={r.id} severity="success" icon={<PictureAsPdfIcon />}>
                  {/*
                    The PDF is streamed by an authenticated route, not served
                    from a public directory — it contains medical detail.
                  */}
                  <MuiLink
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() =>
                      openSignedSlipPdf(
                        () => permissionSlipParentApi.fetchPdf(r.id),
                        (m) => enqueueSnackbar(m, { variant: 'error' })
                      )
                    }
                  >
                    Download {r.Student.firstName}&apos;s signed slip
                  </MuiLink>
                </Alert>
              ))}
            </Stack>
          )}
        </Grid>
      </Grid>

      <Button startIcon={<ArrowBackIcon />} onClick={backToList} sx={{ mt: 3 }}>
        Back to Permission Slips
      </Button>
    </Box>
  );
}
